import { DataValue } from '@apollo/client/react/hoc';
import { gql } from '@apollo/client';
import React from 'react';
import I18n from 'i18n-js';
import { pick } from 'lodash';

import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import { connect } from '@reverbdotcom/commons/src/connect';
import ModalDialog from '@reverbdotcom/commons/src/components/modal_dialog';
import { RCLoadingBars } from '@reverbdotcom/cadence/components';
import { MoneyInput } from '@reverbdotcom/commons/src/components/money_input';
import { useHOCMutation, MutationFunction } from '@reverbdotcom/commons/src/useHOCMutation';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import { formatAmount, parseAmount } from '@reverbdotcom/commons/src/money';
import {
  CoreQuickPriceEditListing,
  CoreQuickPriceEditPriceUpdate,
  reverb_pricing_PriceRecommendation,
  core_apimessages_Money,
  reverb_config_cache_ExchangeRate,
} from '@reverbdotcom/commons/src/rql';

import { buildFormStateFromPersistedListing, Field } from '../../sell/form/sellFormReducer';
import SellFormIncVatPricingHint from '../../sell/form/components/SellFormIncVatPricingHint';
import { HydrateSellFormFragment } from '../../sell/form/hydrateSellFormFragment';
import QuickPriceEditPriceRecommendation from './quick_price_edit_price_recommendation';
import QuickPriceEditPercentOffFilterChips from './quick_price_edit_percent_off_filter_chips';
import { convertCurrency } from '@reverbdotcom/commons/src/convert_currency';
import PriceBreakdown from '../../sell/form/components/PriceBreakdown';
import { LISTING_STATES } from '@reverbdotcom/commons/src/constants';
import QuickPriceEditHelpText, { calculatePercentOff } from './quick_price_edit_help_text';
import { IShopConfig, SHIPPING_PROFILE_CONFIG_FIELDS, SHOP_CONFIG_FIELDS } from '../../sell/form/SellFormContext';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { BRAND_NEW } from '@reverbdotcom/commons/src/condition_helpers';
import { isExperimentEnabled } from '@reverbdotcom/commons/src/user_context_helpers';
import experiments from '@reverbdotcom/commons/src/experiments';

export const COMPONENT_NAME = 'QuickPriceEditModal';

interface IExternalProps {
  listingId: string | number;
  isOpen: boolean;
  requestClose: React.Dispatch<void>;
  onSuccessCallback: React.Dispatch<any>;
  sellerPrice: core_apimessages_Money;
}
interface IApolloProps {
  data: DataValue<CoreQuickPriceEditListing.Query>;
  updatePriceMutation: MutationFunction<CoreQuickPriceEditPriceUpdate.Mutation, CoreQuickPriceEditPriceUpdate.Variables>;
}

type IProps = IApolloProps & IExternalProps;

export function buildShopConfig(data: CoreQuickPriceEditListing.Query): IShopConfig {
  return {
    ...pick(data.listing.shop, SHOP_CONFIG_FIELDS),
    shippingProfiles: data.listing.shop.shippingProfiles.map((sp) => ({
      ...pick(sp, SHIPPING_PROFILE_CONFIG_FIELDS),
      profileRates: sp.allShippingRates,
    })),
  } as IShopConfig;
}

interface IConvertPriceRecArgs {
  priceRecommendation: reverb_pricing_PriceRecommendation;
  rates: reverb_config_cache_ExchangeRate[];
  currency?: string;
}

function convertPriceRecommendation({ priceRecommendation, rates, currency }: IConvertPriceRecArgs) {
  const {
    priceLow,
    priceMiddle,
    priceHigh,
  } = priceRecommendation;

  const isMissingRates = !rates.length;
  const isMissingCurrency = !currency;
  const isMissingPriceRec = !priceLow || !priceMiddle || !priceHigh;

  if (isMissingRates) return;
  if (isMissingCurrency) return;
  if (isMissingPriceRec) return;

  const convertedPriceLow = convertCurrency(
    priceLow.currency,
    currency,
    rates,
    priceLow.amount,
  );

  const convertedPriceMiddle = convertCurrency(
    priceMiddle.currency,
    currency,
    rates, priceMiddle.amount,
  );

  const convertedPriceHigh = convertCurrency(
    priceHigh.currency,
    currency,
    rates,
    priceHigh.amount,
  );

  const displayPriceLow = formatAmount(convertedPriceLow, { precision: 0 });
  const displayPriceMiddle = formatAmount(convertedPriceMiddle, { precision: 0 });
  const displayPriceHigh = formatAmount(convertedPriceHigh, { precision: 0 });

  return {
    priceLow: {
      ...convertedPriceLow,
      ...{ display: displayPriceLow },
    },
    priceMiddle: {
      ...convertedPriceMiddle,
      ...{ display: displayPriceMiddle },
    },
    priceHigh: {
      ...convertedPriceHigh,
      ...{ display: displayPriceHigh },
    },
  };
}
interface IShowFilterChipsArgs {
  priceRecommendation: reverb_pricing_PriceRecommendation,
  sellerPrice: core_apimessages_Money
}

function showQuickPriceEditFilterChips({ priceRecommendation, sellerPrice }: IShowFilterChipsArgs) {
  if (!sellerPrice.amountCents) return false;
  if (!priceRecommendation.priceMiddle) return true;

  return sellerPrice.amountCents > priceRecommendation.priceMiddle.amountCents;
}

export function QuickPriceEditModal({
  data: listingData,
  isOpen,
  listingId,
  requestClose,
  onSuccessCallback,
  sellerPrice,
  updatePriceMutation,
}: IProps) {
  const { currency } = sellerPrice;

  const {
    loading: listingDataLoading,
    listing = {},
    exchangeRates = {},
  } = listingData ?? {};

  const isLiveListing = listing.state == LISTING_STATES.LIVE;

  const [
    mutate,
    {
      loading: mutationDataLoading,
      data: mutationData,
      errors: mutationErrors,
    },
  ] = useHOCMutation(updatePriceMutation);

  const isMissingListingData = !listingData;
  const shopConfig = listingDataLoading || isMissingListingData ? null : buildShopConfig(listingData);
  const preferredSeller = shopConfig?.preferredSeller ?? false;

  const user = useUser();
  const { locale, countryCode } = user;

  const [priceAmount, setPriceAmount] = React.useState(sellerPrice.amount);
  const [errors, setErrors] = React.useState([]);

  const priceRecommendation: reverb_pricing_PriceRecommendation = convertPriceRecommendation({
    priceRecommendation: listing.priceRecommendation ?? {},
    rates: exchangeRates.rates ?? [],
    currency,
  }) ?? {};

  const { priceMiddle: competitivePrice } = priceRecommendation;

  const showFilterChips = showQuickPriceEditFilterChips({
    priceRecommendation,
    sellerPrice,
  });

  const forceMoneySuffixForUserInUS = locale === 'en' && countryCode === 'US' && currency === 'USD';

  const isBrandNew = listingData?.listing?.condition?.conditionSlug === BRAND_NEW;
  const showIncVatPricingHint = shopConfig?.inclusiveVatPricingEnabled && isBrandNew && isExperimentEnabled(user, experiments.INC_VAT_LISTING_PRICE);

  React.useEffect(() => {
    const shouldTrackComponentView = isOpen && !listingDataLoading && !!listingData;

    if (shouldTrackComponentView) {
      trackEvent({
        eventName: MParticleEventName.ComponentView,
        componentName: COMPONENT_NAME,
        listingId,
      });
    }
  }, [listingDataLoading]);

  React.useEffect(() => {
    const isFinishedLoading = !mutationDataLoading;
    const hasData = !!mutationData;
    const hasErrors = !!mutationErrors?.length;
    const hasAPIResponse = isFinishedLoading && (hasData || hasErrors);

    if (!hasAPIResponse) return;

    if (hasErrors) {
      handleErrors(mutationErrors);
    } else {
      const updatedPrice = mutationData.crupdateListing.sellerPrice;

      const priceDroppedAttribute = updatedPrice.amountCents != sellerPrice.amountCents ? {
        priceDropped: updatedPrice.amountCents < sellerPrice.amountCents,
      } : {};

      const discountPercent = calculatePercentOff(sellerPrice, updatedPrice.amount);
      const discountPercentAttribute = discountPercent ? { discountPercent } : {};

      const wellPricedAttribute = competitivePrice ? {
        wellPriced: competitivePrice.amountCents >= updatedPrice.amountCents,
      } : {};

      trackEvent({
        eventName: MParticleEventName.UpdatedListingPrice,
        componentName: COMPONENT_NAME,
        listingId,
        ...priceDroppedAttribute,
        ...discountPercentAttribute,
        ...wellPricedAttribute,
      });

      setErrors([]);
      setPriceAmount(updatedPrice.amount);
      onSuccessCallback(updatedPrice);
    }
  }, [mutationDataLoading]);

  function handleCloseModal() {
    setErrors([]);
    setPriceAmount(sellerPrice.amount);
    requestClose();
  }

  function handleErrors(errors) {
    setErrors(errors.map(error => error.message));
  }

  function formatErrors() {
    return errors.join(' ');
  }

  function updatePrice() {
    const formattedListing = buildFormStateFromPersistedListing(listing);
    const listingEntry = {
      ...formattedListing,
      ...{
        [Field.PRICE]: {
          amount: `${priceAmount}`,
        },
      },
    };

    mutate({
      variables: {
        input: {
          listingEntry,
        },
      },
    });
  }

  if (!isOpen) return null;

  return (
    <ModalDialog
      isOpen={isOpen}
      headerTitle={I18n.t('discovery.listingsManagement.quickPriceEdit.modal.title')}
      onRequestClose={handleCloseModal}
      hideFooterDismissButton
      onSubmit={updatePrice}
      isSaving={mutationDataLoading}
      isDisabled={listingDataLoading}
    >
      {listingDataLoading ? (
        <div className="d-flex fx-justify-center">
          <RCLoadingBars />
        </div>
      ) : (
        <>
          {showFilterChips && (
            <QuickPriceEditPercentOffFilterChips
              listingId={listingId}
              originalPrice={sellerPrice}
              currentPrice={parseAmount(priceAmount, currency)}
              updatePrice={price => setPriceAmount(price.amount)}
            />
          )}

          <MoneyInput
            id={Field.PRICE}
            name={Field.PRICE}
            label={I18n.t('discovery.listingsManagement.quickPriceEdit.modal.moneyInput.label')}
            value={priceAmount || ''}
            onChange={event => setPriceAmount(event.target.value)}
            required
            helpText={<QuickPriceEditHelpText originalPrice={sellerPrice} currentPriceAmount={priceAmount} isLiveListing={isLiveListing} />}
            errorText={formatErrors()}
            currencyCode={currency}
            suffixText={forceMoneySuffixForUserInUS ? currency : null}
          />

          {showIncVatPricingHint &&
            <SellFormIncVatPricingHint />
          }

          <PriceBreakdown
            price={priceAmount}
            currency={currency}
            preferredSeller={preferredSeller}
            sellingFeeWaived={false}
            shopConfig={shopConfig}
            showIncVatPricingHint={showIncVatPricingHint}
          />

          <QuickPriceEditPriceRecommendation
            listingId={listingId}
            priceRecommendation={priceRecommendation}
          />
        </>
      )}
    </ModalDialog>
  );
}

const withListingQuery = withGraphql<IExternalProps, CoreQuickPriceEditListing.Query>(
  gql`
    query Core_QuickPriceEdit_Listing($listingId: String) {
      listing(input: { id: $listingId }) {
        _id
        priceRecommendation {
          priceLow {
            amount
            amountCents
            display
            currency
          }
          priceMiddle {
            amount
            amountCents
            display
            currency
          }
          priceHigh {
            amount
            amountCents
            display
            currency
          }
        }
        ...HydrateSellForm
      }
      exchangeRates(input: { inverseReverseRates: true }) {
        rates {
          from
          to
          rate
        }
      }
    },
    ${HydrateSellFormFragment}
  `,
  {
    skip: ({ isOpen }) => !isOpen,
    options: ({ listingId }) => {
      return {
        ssr: false,
        fetchPolicy: 'network-only',
        variables: { listingId },
        errorPolicy: 'all',
      };
    },
  },
);

const withPriceUpdateMutation = withGraphql<IExternalProps, CoreQuickPriceEditPriceUpdate.Mutation, CoreQuickPriceEditPriceUpdate.Variables>(
  gql`
    mutation Core_QuickPriceEdit_PriceUpdate($input:  Input_core_apimessages_UpdateListingRequest) {
      crupdateListing(input: $input) {
        _id
        sellerPrice {
          amountCents
          currency
          amount
          display
          symbol
        }
      }
    }
  `,
  {
    name: 'updatePriceMutation',
  },
);

export default connect<IExternalProps>([
  withPriceUpdateMutation,
  withListingQuery,
])(QuickPriceEditModal);
